import { CARD_ON_FILE_HASH } from '~/components/Payments/constants';
import { PaymentBlock } from '~/models/blocks/payment-block.model';
import { CXPaymentBlock } from '~/components/ClientExperience/OneFlow/models/cx-payment-block.model';
import { round } from 'lodash';
import { daysDifference } from '~/utils/date.utils';
import sumBy from 'lodash/sumBy';
import type { ServiceItemBlock } from '~/models/blocks/service-item-block.model';
import { DiscountTypes, IInvoiceSummary } from '~/types/model.types';
import { CXServiceItemBlock } from '~/components/ClientExperience/OneFlow/models/cx-service-item-block.model';

export const goToPaymentsSettings = () =>
    (window.location.href = `/app/settings/company/payment-methods${CARD_ON_FILE_HASH}`);

export const calculateLateFee = (payment: PaymentBlock | CXPaymentBlock) => {
    if (payment.isPaid || payment.isPending || payment.isRefunded) {
        const lateFee = payment.connectedVariable?.value?.late_fee_paid || 0;
        return round(lateFee, 2);
    }

    const isFeeEnabled = payment.flow?.invoiceSummaryBlock?.isLateFeeEnabled;

    const applyLateFee =
        isFeeEnabled &&
        !payment.isLateFeeWaived &&
        payment.isOverDue &&
        daysDifference(new Date(), payment.paymentDueDate) > 30;

    if (!applyLateFee) {
        return 0;
    }

    const paymentFee =
        payment.paymentAmount *
        (payment.invoiceBlock?.invoiceSummaryBlock?.lateFeePercentage / 100);
    return round(paymentFee, 2);
};

export function taxAmountWithDiscounts(
    invoiceSummary: IInvoiceSummary,
    subTotal: number,
    servicesWithTax: ServiceItemBlock[] | CXServiceItemBlock[],
    taxNumber: string,
    withPayInFullDiscount: boolean
) {
    return sumBy(
        servicesWithTax,
        (service: ServiceItemBlock | CXServiceItemBlock) => {
            let partialDiscountForServiceItem = 0;
            let isRelativeDiscount =
                invoiceSummary.discount_type === DiscountTypes.Relative;
            if (service.totalPrice !== 0 && subTotal !== 0) {
                partialDiscountForServiceItem = isRelativeDiscount
                    ? service.totalPrice * (invoiceSummary.discount / 100)
                    : (service.totalPrice / subTotal) * invoiceSummary.discount;
            }

            const serviceTotalPriceAfterDiscount =
                service.totalPrice - partialDiscountForServiceItem;

            let partialPayInFullDiscountForServiceItem = 0;
            if (withPayInFullDiscount) {
                partialPayInFullDiscountForServiceItem =
                    invoiceSummary.pay_in_full_discount_type ===
                    DiscountTypes.Relative
                        ? serviceTotalPriceAfterDiscount *
                          (invoiceSummary.pay_in_full_discount / 100)
                        : (service.totalPrice / subTotal) *
                          invoiceSummary.pay_in_full_discount;
            }

            const serviceTotalPriceAfterDiscounts =
                serviceTotalPriceAfterDiscount -
                partialPayInFullDiscountForServiceItem;

            return (
                serviceTotalPriceAfterDiscounts *
                (invoiceSummary[taxNumber].value / 100)
            );
        }
    );
}
